<script setup>
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Icon } from '@iconify/vue'
import emitter from '../utils/emitter'
import { useNotify } from '../composables/useNotify'

const { proxy } = getCurrentInstance()
const { state, dismissNotify, armNotify } = useNotify()
const mounted = ref(false)
const motion = ref('')
const peek = ref(false)
let peekTimer = null
let motionTimer = null

const kind = computed(() => state.type || 'info')
const titles = { success: 'Success', error: 'Error', warning: 'Warning', maintenance: 'Maintenance', info: 'Info' }
const title = computed(() => state.title || titles[kind.value] || 'Info')
const icon = computed(() => {
  if (state.icon) return state.icon
  if (kind.value === 'success') return 'fa6-solid:circle-check'
  if (kind.value === 'error') return 'fa6-solid:circle-xmark'
  if (kind.value === 'warning') return 'fa6-solid:triangle-exclamation'
  if (kind.value === 'maintenance') return 'fa6-solid:wrench'
  return 'fa6-solid:circle-info'
})

function clearPeek() {
  if (peekTimer) {
    window.clearTimeout(peekTimer)
    peekTimer = null
  }
}
function clearMotion() {
  if (motionTimer) {
    window.clearTimeout(motionTimer)
    motionTimer = null
  }
}
function startPeek() {
  clearPeek()
  if (state.persistent && state.open) {
    peekTimer = window.setTimeout(() => { peek.value = true }, 15000)
  }
}

watch(() => state.open, (open) => {
  clearMotion()
  if (open) {
    peek.value = false
    clearPeek()
    motion.value = ''
    mounted.value = true
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!state.open) return
        motion.value = 'in'
        motionTimer = window.setTimeout(() => {
          motionTimer = null
          startPeek()
        }, 400)
      })
    })
    return
  }
  peek.value = false
  clearPeek()
  if (!mounted.value) {
    motion.value = ''
    return
  }
  motion.value = 'out'
  motionTimer = window.setTimeout(() => {
    motionTimer = null
    motion.value = ''
    mounted.value = false
  }, 300)
}, { immediate: true })

function onModel(value) {
  if (!value && state.open) dismissNotify()
}
function messageClick(event) {
  const action = event.target?.closest?.('[data-notify-action]')?.getAttribute('data-notify-action')
  if (!action) return
  event.preventDefault()
  emitter.emit(action)
}
function dismiss() {
  if (state.id != null && !String(state.id).startsWith('premium-drop-')) {
    const hidden = JSON.parse(localStorage.getItem('hiddenNotifications') || '[]')
    if (!hidden.includes(state.id)) hidden.push(state.id)
    localStorage.setItem('hiddenNotifications', JSON.stringify(hidden))
  }
  peek.value = false
  clearPeek()
  dismissNotify()
}

async function loadSiteNotice() {
  try {
    const response = await proxy.$api({ serviceName: 'notification', methodName: 'getActiveNotification' })
    if (!response?.id || !response?.message) return
    const now = new Date()
    const hidden = JSON.parse(localStorage.getItem('hiddenNotifications') || '[]')
    if (now >= new Date(response.startDate) && now <= new Date(response.stopDate) && !hidden.includes(response.id)) {
      const type = ({ 1: 'info', 2: 'warning', 3: 'maintenance' })[String(response.type)] || 'info'
      const { notify } = useNotify()
      notify({
        id: response.id,
        message: response.message,
        type,
        persistent: true,
        html: true,
      })
    }
  } catch (error) {
    console.error('Could not load site notification:', error)
  }
}

onMounted(() => {
  armNotify()
  loadSiteNotice()
})
onBeforeUnmount(() => {
  clearPeek()
  clearMotion()
})
</script>

<template>
  <v-snackbar
    :model-value="mounted"
    location="top center"
    :timeout="-1"
    :transition="false"
    class="nr-notify"
    :class="[
      `nr-notify--${kind}`,
      {
        'nr-notify--peek': peek,
        'nr-notify--in': motion === 'in',
        'nr-notify--out': motion === 'out',
      },
    ]"
    @update:model-value="onModel"
    @mouseenter="peek = false; clearPeek()"
    @mouseleave="startPeek"
  >
    <div class="nr-notify__row">
      <span class="nr-notify__icon" aria-hidden="true">
        <Icon :icon="icon" width="15" height="15" />
      </span>
      <div class="nr-notify__text">
        <div class="nr-notify__title">{{ title }}</div>
        <div
          v-if="state.html"
          class="nr-notify__message"
          :innerHTML="state.message"
          @click="messageClick"
        />
        <div v-else-if="state.message" class="nr-notify__message">{{ state.message }}</div>
      </div>
      <button type="button" class="nr-notify__close" aria-label="Dismiss" @click="dismiss">
        <Icon icon="fa6-solid:xmark" width="12" height="12" />
      </button>
    </div>
  </v-snackbar>
</template>
